import { fetchAllPvSize } from '../../services/allPvSizeService';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface SolarPVCapacityData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  totalCapacity?: number;
  latestYearCapacity?: number;
  latestYear?: number | string;
  percentChange?: number;
  percentChangeSince2015?: number;
  sectors?: string[];
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVCapacitySupabase(location: string): Promise<SolarPVCapacityData> {
  try {

    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Capacity by Sector - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Capacity (MW)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        totalCapacity: 0,
        latestYearCapacity: 0,
        sectors: []
      };
    }

    // Query Supabase by geoid for server-side filtering
    const { data: pvData, error } = await fetchAllPvSize({
      select: '*',
      geofips
    });

    if (error) {
      console.error('Error fetching Solar PV Capacity data:', error);
      throw error;
    }


    if (!pvData || pvData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Solar PV (MW)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Capacity by Sector - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Capacity (MW)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV capacity data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        totalCapacity: 0,
        latestYearCapacity: 0,
        sectors: []
      };
    }

    // Process the data to create chart datasets (like the HTML version)
    const allYears = Array.from(new Set(pvData.map(item => parseInt(item.year)))).sort();
    const years = allYears.filter(year => year <= 2024); // Filter to 2024 and earlier like HTML
    const sectors = Array.from(new Set(pvData.map(item => item.sector))).filter(Boolean);
    
    const datasets = sectors.map((sector, index) => {
      const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#06b6d4'];
      const color = colors[index % colors.length];
      
      const data = years.map(year => {
        const yearData = pvData.filter(item => 
          parseInt(item.year) === year && item.sector === sector
        );
        return yearData.reduce((sum, item) => sum + (parseFloat(item.total_watts) || 0), 0) / 1000000; // Convert W to MW
      });

      return {
        label: sector,
        data: data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        fill: true,
        tension: 0.1
      };
    });

    const chartData = {
      labels: years,
      datasets: datasets
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar PV Capacity by Sector - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Capacity (MW)'
          },
          beginAtZero: true
        }
      }
    };

    // Total installed capacity (cumulative) in MW
    const totalCapacityMw = pvData.reduce(
      (sum, item) => sum + (parseFloat(item.total_watts) || 0),
      0
    ) / 1000000;
    const latestYear = Math.max(...years);
    const baselineYear = Math.min(...years);
    const latestYearData = pvData.filter((item) => parseInt(item.year) === latestYear);
    const baselineYearData = pvData.filter((item) => parseInt(item.year) === baselineYear);
    const latestYearCapacityMw =
      latestYearData.reduce(
        (sum, item) => sum + (parseFloat(item.total_watts) || 0),
        0
      ) / 1000000;
    const baselineYearCapacityMw =
      baselineYearData.reduce(
        (sum, item) => sum + (parseFloat(item.total_watts) || 0),
        0
      ) / 1000000;

    // % change in total installed (cumulative) capacity since baseline year
    const percentChange =
      baselineYearCapacityMw > 0
        ? ((totalCapacityMw - baselineYearCapacityMw) / baselineYearCapacityMw) * 100
        : totalCapacityMw > 0
          ? 100
          : 0;

    // % change since 2015 specifically
    const year2015Data = pvData.filter((item) => parseInt(item.year) === 2015);
    const year2015CapacityMw =
      year2015Data.reduce(
        (sum, item) => sum + (parseFloat(item.total_watts) || 0),
        0
      ) / 1000000;
    const percentChangeSince2015 =
      year2015CapacityMw > 0
        ? ((latestYearCapacityMw - year2015CapacityMw) / year2015CapacityMw) * 100
        : latestYearCapacityMw > 0
          ? 100
          : 0;

    const trendDirection =
      percentChangeSince2015 > 0 ? 'increased' : percentChangeSince2015 < 0 ? 'decreased' : 'remained stable';
    const changeWord =
      percentChangeSince2015 > 0 ? 'increase' : percentChangeSince2015 < 0 ? 'decrease' : 'change';
    const trendInsight =
      percentChangeSince2015 > 0
        ? ' This increase may reflect falling technology costs, state incentive programs, net energy metering policies, or local climate action efforts.'
        : percentChangeSince2015 < 0
          ? ' This decrease may reflect market saturation, policy changes, reduced incentives, or barriers such as permitting challenges.'
          : ' This may reflect market saturation, policy changes, reduced incentives, or barriers such as permitting challenges.';

    const narrative = `As of ${latestYear}, total installed solar capacity in ${location} was ${latestYearCapacityMw.toFixed(
      2
    )} MW. This represents a ${changeWord} of ${Math.abs(percentChangeSince2015).toFixed(
      1
    )}% since 2015. Over time, solar capacity has generally ${trendDirection}. Solar PV systems generate electricity from sunlight and are one of the most common forms of renewable energy used in homes and businesses. Tracking solar installations shows how quickly a community is adopting clean energy technologies and reducing its reliance on fossil-fuel-based electricity.${trendInsight}`;

    return {
      chartData,
      chartOptions,
      narrative,
      totalCapacity: totalCapacityMw,
      latestYearCapacity: latestYearCapacityMw,
      latestYear: latestYear.toString(),
      percentChange,
      percentChangeSince2015,
      sectors
    };

  } catch (error) {
    console.error('Error in fetchSolarPVCapacitySupabase:', error);
    throw error;
  }
}
